import { MimeType } from '@sonar-software/sonar-ts-types';
import { getRadiansFromDegrees, transformDimensionWithRotation } from '../math/lib';

type Area = {
	height: number;
	width: number;
	x: number;
	y: number;
};

/**
 * Asyncronously builds a new {@link HTMLImageElement} from the raw image source string provided.
 * @param imageSource Raw data string of an image to build.
 * @returns A promise to return a new ${@link HTMLImageElement} upon success.
 */
export function createImageFromSource(imageSource: string): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const image = new Image();

		image.addEventListener('load', () => resolve(image));
		image.addEventListener('error', reject);

		image.src = imageSource;
	});
}

/**
 * Generates a new image {@link Blob} from the original image data and provided crop and rotation.
 * @param originalImageSource Raw data string of unmodified image.
 * @param originalImageType Original ${@link MimeType} of the unmodified image.
 * @param cropArea Cropping dimensions for the image defined as an {@link Area}.
 * @param rotation Rotation amount for the image in degrees.
 * @returns A promise to return a new image {@link Blob} that is cropped and rotated.
 */
export async function createCroppedImage(
	originalImageSource: string,
	originalImageType: MimeType | undefined,
	cropArea: Area,
	rotation: number
): Promise<Blob | null> {
	const originalImage = await createImageFromSource(originalImageSource);

	const canvas = document.createElement('canvas');
	const context = canvas.getContext('2d');

	if (!originalImage || !context) {
		return null;
	}

	const boundingBoxDimensions = transformDimensionWithRotation(
		{
			height: originalImage.height,
			width: originalImage.width
		},
		rotation
	);

	canvas.height = boundingBoxDimensions.height;
	canvas.width = boundingBoxDimensions.width;

	context.translate(boundingBoxDimensions.width / 2, boundingBoxDimensions.height / 2);
	context.rotate(getRadiansFromDegrees(rotation));
	context.translate(-originalImage.width / 2, -originalImage.height / 2);
	context.drawImage(originalImage, 0, 0);

	const cropData = context.getImageData(cropArea.x, cropArea.y, cropArea.width, cropArea.height);

	canvas.height = cropArea.height;
	canvas.width = cropArea.width;

	context.putImageData(cropData, 0, 0);

	return new Promise((resolve) => {
		canvas.toBlob(resolve, originalImageType);
	});
}
